'use client'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

type ModalProps = {
  isOpen: boolean
  onClose?: () => void
  blur?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export default function Modal({ isOpen, onClose, children, className, ...props }: ModalProps) {
  const [isAnimating, setIsAnimating] = useState(false)

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.documentElement.style.overflow = 'hidden'
      setIsAnimating(true)
      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.documentElement.style.overflow = ''
      }
    } else {
      setIsAnimating(false)
    }
  }, [isOpen, handleEscape])

  if (!isOpen) return null

  return createPortal(
    <div
      className={cn(
        `fixed inset-0 z-[9999] flex items-center justify-center`,
        `bg-black/40 backdrop-blur-sm transition-opacity duration-200`,
        isAnimating ? 'opacity-100' : 'opacity-0'
      )}
      onClick={onClose ?? undefined}
    >
      <div
        className={cn(
          'pointer-events-auto transition-transform duration-200',
          isAnimating ? 'translate-y-0' : 'translate-y-4',
          className
        )}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}
